import { FC, useCallback, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import {
  ActivityIndicator,
  Button,
  DropdownMenu,
  Icon,
  IconButton,
  Toggle,
} from '@kontentino/ui';
import { faArrowDownToLine } from '@fortawesome/pro-regular-svg-icons/faArrowDownToLine';
import { faArrowRightArrowLeft } from '@fortawesome/pro-regular-svg-icons/faArrowRightArrowLeft';
import { faChevronRight } from '@fortawesome/pro-regular-svg-icons/faChevronRight';
import { faPlus } from '@fortawesome/pro-regular-svg-icons/faPlus';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { ApiClientError } from 'api/client';
import type { ReportFormat } from 'api/analytics-reports/types';
import { useToast } from 'app/hooks/useToast';
import { addQueryParams } from 'utils/url';
import { modalRoutes } from 'constants/routes';
import AnalyticsDatePicker from 'app/modules/analytics/components/AnalyticsDatePicker';
import {
  COMPARISON_PRESET_LABEL_KEYS,
  COMPARISON_PRESET_ORDER,
  ComparisonPreset,
  resolveComparisonRange,
} from 'app/modules/analytics/utils/comparisonPresets';
import { useAnalyticsContext } from 'app/modules/analytics/context/AnalyticsContext';
import { useAnalyticsSelection } from 'app/modules/analytics/hooks/useAnalyticsSelection';
import { useAnalyticsHealthQuery } from 'app/modules/analytics/hooks/useAnalyticsHealthQuery';
import { useAnalyticsFiltersStore } from 'app/modules/analytics/store/analyticsFiltersStore';
import { useAnalyticsContentHeaderSync } from 'app/modules/analytics/hooks/useAnalyticsContentHeaderSync';
import { useAnalyticsFetchingHint } from 'app/modules/analytics/hooks/useAnalyticsFetchingHint';
import { useAnalyticsSectionExport } from 'app/modules/analytics/hooks/useAnalyticsSectionExport';
import { useQuickAnalyticsReportExport } from 'app/modules/analytics/modules/reports/hooks/useQuickAnalyticsReportExport';
import type { QuickReportScope } from 'app/modules/analytics/modules/reports/constants/quickReportDefaults';
import { buildAnalyticsReportCreateUrl } from 'app/modules/analytics/modules/reports/utils/analyticsReportsRoutes';
import { useSocialLoginContext } from 'app/modules/socialLogin/context';
import { getSortedAnalyticsPerformancePagesFromGroupPages } from 'app/modules/analytics/utils/profileGroupPageToPageData';
import { normalizeAnalyticsPageIds } from 'app/modules/analytics/utils/normalizeAnalyticsPageIds';
import { resolvePerformanceCsvCompareParams } from 'app/modules/analytics/utils/resolvePerformanceCsvCompareParams';
import { pageTypeToAnalyticsPlatform } from 'app/modules/analytics/modules/content/utils/analyticsPlatformToPageType';
import routeConfig from 'app/config/routes';
import AnalyticsSyncButton from 'app/modules/analytics/components/AnalyticsSyncButton';
import PageTypeUtils from 'app/utils/pageType';
import routes from 'constants/routes';

const { content, performance } = routeConfig.analytics.routes;

interface PerformanceCompareParams {
  compareSince?: string;
  compareUntil?: string;
}

const QUICK_EXPORT_FORMATS: ReportFormat[] = ['pdf', 'csv'];

interface PerformanceReportDownloadSubmenuProps {
  scope: QuickReportScope;
  label: string;
  onExport: (scope: QuickReportScope, format: ReportFormat) => void;
}

const PerformanceReportDownloadSubmenu: FC<
  PerformanceReportDownloadSubmenuProps
> = ({ scope, label, onExport }) => {
  const { t } = useTranslation();

  return (
    <DropdownMenu.Sub>
      <DropdownMenu.SubTrigger>
        <DropdownMenu.Item>
          <Icon icon={faArrowDownToLine} />
          {label}
          <Icon
            size="sm"
            className="tw-ml-auto tw-text-grayscale-100"
            icon={faChevronRight}
          />
        </DropdownMenu.Item>
      </DropdownMenu.SubTrigger>
      <DropdownMenu.Portal>
        <DropdownMenu.SubContent className="tw-z-50 tw-min-w-[160px]">
          {QUICK_EXPORT_FORMATS.map((format) => (
            <DropdownMenu.Item
              key={format}
              onClick={() => onExport(scope, format)}
            >
              <Icon icon={faArrowDownToLine} />
              {t('exportAs', { type: format.toUpperCase() })}
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.SubContent>
      </DropdownMenu.Portal>
    </DropdownMenu.Sub>
  );
};

const AnalyticsContentHeader: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const { timezone } = useAnalyticsContext();
  const {
    selectedPage,
    isGroupSelected,
    enabledGroupPageIds,
    selectedProfileGroup,
  } = useAnalyticsSelection();
  const { isLoading: isHealthLoading } = useAnalyticsHealthQuery();
  // Filters read straight from the store: a date change re-renders this header
  // without touching the 50-odd other context consumers.
  const startDate = useAnalyticsFiltersStore((state) => state.startDate);
  const endDate = useAnalyticsFiltersStore((state) => state.endDate);
  const datePreset = useAnalyticsFiltersStore((state) => state.datePreset);
  const setDateRange = useAnalyticsFiltersStore((state) => state.setDateRange);
  const isComparisonEnabled = useAnalyticsFiltersStore(
    (state) => state.isComparisonEnabled,
  );
  const comparisonStartDate = useAnalyticsFiltersStore(
    (state) => state.comparisonStartDate,
  );
  const comparisonEndDate = useAnalyticsFiltersStore(
    (state) => state.comparisonEndDate,
  );
  const comparisonDatePreset = useAnalyticsFiltersStore(
    (state) => state.comparisonDatePreset,
  );
  const setComparisonDateRange = useAnalyticsFiltersStore(
    (state) => state.setComparisonDateRange,
  );
  const setComparisonEnabled = useAnalyticsFiltersStore(
    (state) => state.setComparisonEnabled,
  );
  const {
    requestSync,
    isSyncing,
    isShowingSuccess,
    selectedPageHealth,
    showReconnectMode,
    syncButtonState,
    syncTestIds,
    trackingDisabledAt,
  } = useAnalyticsContentHeaderSync();
  const isFetchingHintVisible = useAnalyticsFetchingHint();
  const { downloadSectionCsv, isExporting: isCsvExporting } =
    useAnalyticsSectionExport();
  const {
    exportQuickReport,
    isExporting: isQuickReportExporting,
    isTemplatesLoading,
  } = useQuickAnalyticsReportExport();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [isCsvCompareResolvePending, setIsCsvCompareResolvePending] =
    useState(false);
  const socialLogin = useSocialLoginContext();

  const section = useMemo(() => {
    if (pathname.endsWith(content.path)) return 'content';
    if (pathname.endsWith(performance.path)) return 'performance';
    return 'overview';
  }, [pathname]);

  const sortedGroupAnalyticsPages = useMemo(
    () =>
      getSortedAnalyticsPerformancePagesFromGroupPages(
        selectedProfileGroup?.pages,
      ),
    [selectedProfileGroup?.pages],
  );

  const groupExportPageIds = useMemo(() => {
    const enabled = sortedGroupAnalyticsPages.filter((p) =>
      enabledGroupPageIds.has(p.id),
    );
    return normalizeAnalyticsPageIds(enabled.map((p) => p.id));
  }, [sortedGroupAnalyticsPages, enabledGroupPageIds]);

  const isReportMenuBusy =
    section === 'content'
      ? isCsvExporting ||
        isCsvCompareResolvePending ||
        isQuickReportExporting ||
        isTemplatesLoading
      : section === 'overview'
      ? isQuickReportExporting || isTemplatesLoading
      : isQuickReportExporting || isTemplatesLoading;

  const handleDownloadCsv = useCallback(async () => {
    const sortedSinglePageIds = selectedPage
      ? normalizeAnalyticsPageIds([selectedPage.id])
      : [];

    setIsCsvCompareResolvePending(true);
    const csvSection = section as 'performance' | 'content';
    try {
      const compare: PerformanceCompareParams =
        await resolvePerformanceCsvCompareParams(queryClient, {
          section: csvSection,
          timezone,
          isComparisonEnabled,
          comparisonStartDate,
          comparisonEndDate,
          startDate,
          endDate,
          sortedPageIds: isGroupSelected
            ? groupExportPageIds
            : sortedSinglePageIds,
        });

      if (isGroupSelected) {
        await downloadSectionCsv({
          section: csvSection,
          pageIds: groupExportPageIds,
          compareSince: compare.compareSince,
          compareUntil: compare.compareUntil,
        });
        return;
      }
      if (selectedPage) {
        await downloadSectionCsv({
          section: csvSection,
          platform: pageTypeToAnalyticsPlatform(selectedPage.type),
          pageIds: [selectedPage.id],
          compareSince: compare.compareSince,
          compareUntil: compare.compareUntil,
        });
      }
    } catch (e) {
      const err = e as ApiClientError;
      toast(
        err?.userMessage ?? err?.message ?? t('somethingWentWrong'),
        'error',
      );
    } finally {
      setIsCsvCompareResolvePending(false);
    }
  }, [
    comparisonEndDate,
    comparisonStartDate,
    downloadSectionCsv,
    groupExportPageIds,
    isComparisonEnabled,
    isGroupSelected,
    queryClient,
    section,
    selectedPage,
    startDate,
    endDate,
    timezone,
    toast,
    t,
  ]);

  const handleQuickReportExport = useCallback(
    (scope: QuickReportScope, format: ReportFormat) => {
      void exportQuickReport({ scope, format });
    },
    [exportQuickReport],
  );

  // Landscape export — additive next to the existing report downloads. Remove
  // this handler and its menu item to take the feature back out.
  //
  // Scoped `full` on both sections on purpose: the export renders whichever
  // sections the report carries, and there is no content-only QuickReportScope,
  // so a section-specific label would promise something it cannot deliver.
  const handleLandscapeExport = useCallback(() => {
    void exportQuickReport({
      scope: 'full',
      format: 'pdf',
      layout: 'landscape',
    });
  }, [exportQuickReport]);

  const handleSwapDateRanges = useCallback(() => {
    const prevPrimary = { startDate, endDate };
    const prevComparison = {
      startDate: comparisonStartDate,
      endDate: comparisonEndDate,
    };
    setDateRange(prevComparison, undefined);
    setComparisonDateRange(prevPrimary, undefined);
  }, [
    startDate,
    endDate,
    comparisonStartDate,
    comparisonEndDate,
    setDateRange,
    setComparisonDateRange,
  ]);

  const comparisonPresets = useMemo(
    () =>
      COMPARISON_PRESET_ORDER.map((value) => ({
        value,
        label: t(COMPARISON_PRESET_LABEL_KEYS[value]),
        isDisabled: resolveComparisonRange(value, startDate, endDate) === null,
      })),
    [t, startDate, endDate],
  );

  const handleComparisonPresetSelect = useCallback(
    (value: string) => {
      const range = resolveComparisonRange(
        value as ComparisonPreset,
        startDate,
        endDate,
      );

      if (range) {
        setComparisonDateRange(range, value);
      }
    },
    [startDate, endDate, setComparisonDateRange],
  );

  const handleCreateCustomReport = useCallback(() => {
    const returnTo = `${pathname}${search}`;

    if (section === 'performance') {
      navigate(buildAnalyticsReportCreateUrl({ metricPrefill: 'template' }), {
        state: { returnTo },
      });
      return;
    }

    navigate(routes.ANALYTICS_REPORTS_CREATE, { state: { returnTo } });
  }, [navigate, pathname, search, section]);

  const handleReconnect = useCallback(() => {
    if (!selectedPage) return;
    socialLogin.authorize({
      pageId: selectedPage.id,
      pageType: selectedPage.type,
      redirectUrl: `${addQueryParams(window.location.href, {
        id: selectedPage.id,
        type: selectedPage.type,
        reconnect: 1,
      })}#${modalRoutes.ASSOCIATE_POSTS_PROFILE}`,
    });
  }, [socialLogin, selectedPage]);

  const syncPlatformName = useMemo(
    () =>
      selectedPage != null
        ? PageTypeUtils.getLabel(selectedPage.type)
        : undefined,
    [selectedPage],
  );

  const isSyncStatusPending = useMemo(
    () => isHealthLoading || (!isGroupSelected && selectedPage == null),
    [isHealthLoading, isGroupSelected, selectedPage],
  );

  if (
    isGroupSelected &&
    selectedProfileGroup &&
    sortedGroupAnalyticsPages.length === 0
  ) {
    return null;
  }

  return (
    <div className="tw-relative tw-flex tw-flex-col">
      <div className="tw-flex tw-items-center tw-justify-between tw-py-4 tw-pl-[1px]">
        <div className="tw-flex tw-items-center tw-gap-3">
          <AnalyticsDatePicker
            datePreset={datePreset}
            startDate={startDate}
            endDate={endDate}
            onChange={setDateRange}
          />
          {section === 'performance' && (
            <>
              {isComparisonEnabled && (
                <>
                  <IconButton
                    variant="plain"
                    size="small"
                    icon={<Icon icon={faArrowRightArrowLeft} size="sm" />}
                    onClick={handleSwapDateRanges}
                    data-cy="analytics_content-header_swap-date-ranges"
                    data-name="analytics_content-header_swap-date-ranges"
                  />
                  <AnalyticsDatePicker
                    datePreset={comparisonDatePreset}
                    startDate={comparisonStartDate}
                    endDate={comparisonEndDate}
                    onChange={setComparisonDateRange}
                    primaryPeriod={{ startDate, endDate }}
                    comparisonPresets={comparisonPresets}
                    onComparisonPresetSelect={handleComparisonPresetSelect}
                  />
                </>
              )}
              <div className="tw-flex tw-items-center tw-gap-2 tw-pl-2">
                <Toggle
                  checked={isComparisonEnabled}
                  onChange={() => setComparisonEnabled(!isComparisonEnabled)}
                  data-cy="analytics_content-header_compare-toggle"
                />
                <span className="tw-text-sm tw-text-grayscale-180">
                  {t('analyticsCompare')}
                </span>
              </div>
            </>
          )}
          {isFetchingHintVisible && (
            <div
              className="tw-flex tw-items-center tw-gap-2 tw-pl-2 tw-text-sm tw-text-grayscale-100"
              data-cy="analytics_content-header_refetch-indicator"
              data-name="analytics_content-header_refetch-indicator"
            >
              <ActivityIndicator />
              <span>{t('analyticsLoadingData')}</span>
            </div>
          )}
        </div>
        <div className="tw-flex tw-items-center tw-gap-2">
          {!isSyncing && (
            <AnalyticsSyncButton
              state={syncButtonState}
              lastSyncedAt={selectedPageHealth?.last_successful_sync_at}
              isSyncStatusPending={isSyncStatusPending}
              trackingDisabledAt={trackingDisabledAt}
              platformName={syncPlatformName}
              isGroup={isGroupSelected}
              onClick={
                showReconnectMode
                  ? handleReconnect
                  : () => requestSync({ userInitiated: true })
              }
              disabled={isShowingSuccess || syncButtonState === 'sync-paused'}
              dataCy={syncTestIds.dataCy}
              dataName={syncTestIds.dataName}
            />
          )}
          <DropdownMenu.Root>
            <DropdownMenu.Trigger>
              <Button
                variant="secondary"
                size="medium"
                className="tw-h-[36px] tw-border-transparent tw-bg-grayscale-100 tw-text-white hover:tw-bg-grayscale-120 hover:tw-text-white disabled:tw-opacity-60 [&_svg]:tw-text-white"
                iconBefore={<Icon icon={faArrowDownToLine} />}
                isLoading={isReportMenuBusy}
                disabled={isReportMenuBusy}
                data-cy="analytics_content-header_export-menu-trigger"
                data-name="analytics_content-header_export-menu-trigger"
              >
                {t('analyticsReports.addReport')}
              </Button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content className="tw-z-50 tw-min-w-[240px]">
                {section === 'performance' ? (
                  <>
                    <PerformanceReportDownloadSubmenu
                      scope="full"
                      label={t('analyticsReports.downloadFullReport')}
                      onExport={handleQuickReportExport}
                    />
                    <PerformanceReportDownloadSubmenu
                      scope="performance"
                      label={t('analyticsReports.downloadPerformanceReport')}
                      onExport={handleQuickReportExport}
                    />
                    <DropdownMenu.Item
                      onClick={handleLandscapeExport}
                      data-cy="analytics_content-header_export-landscape-pdf"
                      data-name="analytics_content-header_export-landscape-pdf"
                    >
                      <Icon icon={faArrowDownToLine} />
                      {t('analyticsReports.exportLandscapePdf')}
                    </DropdownMenu.Item>
                    <DropdownMenu.Item
                      onClick={handleCreateCustomReport}
                      className="tw-text-primary-100"
                      data-cy="analytics_content-header_create-custom-report"
                      data-name="analytics_content-header_create-custom-report"
                    >
                      <Icon icon={faPlus} className="tw-text-primary-100" />
                      {t('analyticsReports.createCustomReport')}
                    </DropdownMenu.Item>
                  </>
                ) : section === 'overview' ? (
                  <>
                    {/* TODO(overview-pdf-be): export používa growth_pulse workaround; PDF layout/overview posty až po BE. */}
                    <DropdownMenu.Item
                      onClick={() => handleQuickReportExport('overview', 'pdf')}
                      data-cy="analytics_content-header_download-overview-report-pdf"
                      data-name="analytics_content-header_download-overview-report-pdf"
                    >
                      <Icon icon={faArrowDownToLine} />
                      {t('analyticsReports.downloadOverviewReport')}
                    </DropdownMenu.Item>
                    <DropdownMenu.Item
                      onClick={handleCreateCustomReport}
                      className="tw-text-primary-100"
                      data-cy="analytics_content-header_create-custom-report"
                      data-name="analytics_content-header_create-custom-report"
                    >
                      <Icon icon={faPlus} className="tw-text-primary-100" />
                      {t('analyticsReports.createCustomReport')}
                    </DropdownMenu.Item>
                  </>
                ) : (
                  <>
                    <PerformanceReportDownloadSubmenu
                      scope="full"
                      label={t('analyticsReports.downloadFullReport')}
                      onExport={handleQuickReportExport}
                    />
                    <DropdownMenu.Item
                      onClick={handleDownloadCsv}
                      data-cy="analytics_content-header_export-csv"
                      data-name="analytics_content-header_export-csv"
                    >
                      <Icon icon={faArrowDownToLine} />
                      {t('analyticsContent.downloadContentReport')}
                    </DropdownMenu.Item>
                    <DropdownMenu.Item
                      onClick={handleLandscapeExport}
                      data-cy="analytics_content-header_export-landscape-pdf"
                      data-name="analytics_content-header_export-landscape-pdf"
                    >
                      <Icon icon={faArrowDownToLine} />
                      {t('analyticsReports.exportLandscapePdf')}
                    </DropdownMenu.Item>
                    <DropdownMenu.Item
                      onClick={handleCreateCustomReport}
                      className="tw-text-primary-100"
                      data-cy="analytics_content-header_create-custom-report"
                      data-name="analytics_content-header_create-custom-report"
                    >
                      <Icon icon={faPlus} className="tw-text-primary-100" />
                      {t('analyticsReports.createCustomReport')}
                    </DropdownMenu.Item>
                  </>
                )}
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        </div>
      </div>
      <div
        className="bg-gradient-primary tw-pointer-events-none tw-absolute tw-inset-x-0 tw-top-full tw-h-5 tw-backdrop-blur-sm [-webkit-mask-image:linear-gradient(to_bottom,black,transparent)] [mask-image:linear-gradient(to_bottom,black,transparent)]"
        aria-hidden
      />
    </div>
  );
};

export default AnalyticsContentHeader;
